/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useAsyncSelectTurnaround.js
 *
 * Purpose: Computes assay-scoped filter params for the Turnaround async select field.
 *
 * Scope: Turnaround field selection within the order request assay/panel test flow.
 *
 * Tags: customer-portal, turnarounds
 *
 * Overview: Reads the filterTATByAssayEnabled app setting and the assay or panel
 *     being added to an order request. When the field being rendered is the Test entity's
 *     Turnaround reference and the feature flag is on, builds a memoized params object holding
 *     the relevant assay_ids (a single id for an assay, or the list of panel assay ids for a
 *     panel). Returns undefined when the flag is off, the field isn't a test turnaround field,
 *     or no assay/panel context is available, letting the caller fall back to an unfiltered
 *     turnaround list.
 *
 * Exports: useAsyncSelectTurnaround hook.
 */

import { useAppStore } from 'App/store';
import { useOrderRequestStore } from 'Order/store';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { EntityType } from 'shared/constants/entityTypes';
import { isPanelType } from '../../utils/misc';

export function useAsyncSelectTurnaround({
  entityType,
  fieldTypeEntityReference,
} = {}) {
  const {
    appState: { filterTATByAssayEnabled },
  } = useAppStore();
  const { assayToAdd: assayOrPanel } = useOrderRequestStore() || {};
  const isPanel = isPanelType(assayOrPanel);

  // Turnaround needs a special handling when
  // `FILTER_TAT_BY_ASSAY_ENABLED` is on.
  const assayId = assayOrPanel?.id;
  const isTestTurnaround =
    entityType === EntityType.TEST &&
    fieldTypeEntityReference === EntityType.TURNAROUND;

  const turnaroundParams = useMemo(() => {
    if (
      !filterTATByAssayEnabled ||
      !isTestTurnaround ||
      !assayId ||
      isEmpty(assayOrPanel)
    )
      return;
    return {
      assay_ids: isPanel
        ? (assayOrPanel.panel_assays || []).map((pa) => pa.assay_id)
        : assayId,
    };
  }, [
    isTestTurnaround,
    filterTATByAssayEnabled,
    assayOrPanel,
    assayId,
    isPanel,
  ]);

  return turnaroundParams;
}
